// Geolocation utilities

export const STATE_NAMES: Record<string, string> = {
  AC: "Acre", AL: "Alagoas", AP: "Amapá", AM: "Amazonas", BA: "Bahia",
  CE: "Ceará", DF: "Distrito Federal", ES: "Espírito Santo", GO: "Goiás",
  MA: "Maranhão", MT: "Mato Grosso", MS: "Mato Grosso do Sul", MG: "Minas Gerais",
  PA: "Pará", PB: "Paraíba", PR: "Paraná", PE: "Pernambuco", PI: "Piauí",
  RJ: "Rio de Janeiro", RN: "Rio Grande do Norte", RS: "Rio Grande do Sul",
  RO: "Rondônia", RR: "Roraima", SC: "Santa Catarina", SP: "São Paulo",
  SE: "Sergipe", TO: "Tocantins",
};

export function stateName(uf: string): string {
  if (!uf) return "";
  return STATE_NAMES[uf.toUpperCase()] || uf;
}

export function slugify(text: unknown): string {
  if (!text) return "";
  return text
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function unslugify(slug: string): string {
  if (!slug) return "";
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
}

export interface GeoPoint {
  lat: number;
  lng: number;
}

// Haversine distance in km between two lat/lng points
export function haversineKm(a: GeoPoint | null | undefined, b: GeoPoint | null | undefined): number | null {
  if (!a || !b || a.lat == null || a.lng == null || b.lat == null || b.lng == null) return null;
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

interface GetMyLocationOpts {
  timeout?: number;
  maximumAge?: number;
}

/**
 * Browser geolocation with generous timeouts and retries.
 * Default 8s was too tight on mobile / weak GPS — users saw code 3 (TIMEOUT) often.
 */
export async function getMyLocation(opts: number | GetMyLocationOpts = {}): Promise<GeoPoint> {
  let primaryTimeout = 22_000;
  let maximumAge = 300_000; // 5 min — reuse a recent fix when possible (faster, fewer timeouts)

  if (typeof opts === "number") {
    primaryTimeout = opts;
  } else if (opts && typeof opts === "object") {
    if (opts.timeout != null) primaryTimeout = opts.timeout;
    if (opts.maximumAge != null) maximumAge = opts.maximumAge;
  }

  if (!("geolocation" in navigator)) {
    throw new Error("Geolocalização não disponível deste navegador.");
  }

  // Pre-check permission state to avoid triggering CoreLocation errors
  // (e.g. kCLErrorLocationUnknown) when permission is already denied.
  try {
    const perm = await navigator.permissions.query({ name: "geolocation" });
    if (perm.state === "denied") {
      throw new Error("Permissão de localização negada. Habilite no navegador.");
    }
  } catch (e: unknown) {
    if (e instanceof Error && e.message.startsWith("Permissão de localização")) throw e;
    // permissions API not available or query failed — proceed normally
  }

  const read = (
    timeoutMs: number,
    maxAgeMs: number,
    enableHighAccuracy: boolean,
  ): Promise<GeoPoint> =>
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => reject(err),
        { enableHighAccuracy, timeout: timeoutMs, maximumAge: maxAgeMs },
      );
    });

  const retriable = (code: number) => code === 0 || code === 2 || code === 3; // UNKNOWN, POSITION_UNAVAILABLE, TIMEOUT

  try {
    return await read(primaryTimeout, maximumAge, false);
  } catch (e: unknown) {
    if (!retriable((e as { code?: number })?.code ?? 0)) throw e;
  }
  try {
    return await read(38_000, Math.max(maximumAge, 600_000), false);
  } catch (e: unknown) {
    if (!retriable((e as { code?: number })?.code ?? 0)) throw e;
  }
  // Last resort: high accuracy (GPS) — slower but sometimes succeeds when IP/cell fails
  return read(28_000, Math.max(maximumAge, 600_000), true);
}

const KEY = "user_location";
const TTL_MS = 6 * 60 * 60 * 1000; // 6 hours — fresh enough for distance/region

export function loadStoredLocation(): GeoPoint | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.lat !== "number" || typeof parsed.lng !== "number") return null;
    if (typeof parsed.savedAt === "number" && Date.now() - parsed.savedAt > TTL_MS) {
      try { localStorage.removeItem(KEY); } catch { /* ignore */ }
      return null;
    }
    return { lat: parsed.lat, lng: parsed.lng };
  } catch {
    /* ignore */
  }
  return null;
}
export function storeLocation(loc: GeoPoint): void {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ lat: loc.lat, lng: loc.lng, savedAt: Date.now() }),
    );
  } catch {
    /* ignore */
  }
}
export function clearLocation(): void { try { localStorage.removeItem(KEY); } catch { /* ignore */ } }

interface LocationOption {
  value: string;
  label: string;
}

export interface GeocodedFilters {
  state: string;
  city: string;
  neighborhood: string;
}

interface ApiLike {
  get(path: string, opts?: { params?: Record<string, unknown> }): Promise<{ data: unknown }>;
}

export async function getGeocodedFilters(lat: number, lng: number, api: ApiLike): Promise<GeocodedFilters> {
  // Reverse-geocode, then validate the resulting state/city against the canonical
  // location sources (states + IBGE city list) so we don't depend on existing
  // listings — which may be empty for a city, leaving nothing to match. The
  // neighborhood is free-text, so the reverse-geocoded value is passed through.
  try {
    const rev = (await api.get("/geocode/reverse", { params: { lat, lng } })).data as { state?: string; city?: string; neighborhood?: string };
    if (!rev.state) return { state: "", city: "", neighborhood: "" };

    const foundState = matchOption(await fetchStatesCached(api), rev.state);
    const matchState = foundState ? foundState.value : rev.state;

    if (!rev.city) return { state: matchState, city: "", neighborhood: rev.neighborhood || "" };

    const foundCity = matchOption(await fetchCitiesCached(api, matchState), rev.city);
    const matchCity = foundCity ? foundCity.value : rev.city;

    return { state: matchState, city: matchCity, neighborhood: rev.neighborhood || "" };
  } catch {
    return { state: "", city: "", neighborhood: "" };
  }
}

let _cachedStates: Promise<LocationOption[]> | null = null;
function fetchStatesCached(api: ApiLike): Promise<LocationOption[]> {
  if (_cachedStates) return _cachedStates;
  _cachedStates = api.get("/locations/states").then((r) => r.data as LocationOption[]);
  return _cachedStates;
}
const _citiesCache: Record<string, Promise<LocationOption[]>> = {};
function fetchCitiesCached(api: ApiLike, state: string): Promise<LocationOption[]> {
  if (!state) return Promise.resolve([]);
  if (!_citiesCache[state]) _citiesCache[state] = api.get(`/locations/cities/${state}`).then((r) => r.data as LocationOption[]);
  return _citiesCache[state];
}
function matchOption(list: LocationOption[] | null, target: string): LocationOption | null {
  if (!target || !list) return null;
  const targetSlug = slugify(target);
  return list.find((o) => slugify(o.value) === targetSlug || slugify(o.label) === targetSlug) || null;
}